import React, { useState } from 'react';
import styled from 'styled-components';

// Styled Components
const PageContainer = styled.div`
  display: flex;
  height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
`;

const LeftPanel = styled.div`
  width: 350px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-right: 1px solid rgba(255, 255, 255, 0.2);
  padding: 20px;
  overflow-y: auto;
`;

const RightPanel = styled.div`
  flex: 1;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Title = styled.h1`
  color: white;
  text-align: center;
  margin-bottom: 30px;
  font-size: 2.5rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
`;

const Controls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ControlGroup = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 15px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const Label = styled.label`
  display: block;
  color: white;
  margin-bottom: 8px;
  font-weight: 600;
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: none;
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  margin-bottom: 10px;
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 5px;
  background: ${props => props.$variant === 'danger' ? '#ff6b6b' : '#4ecdc4'};
  color: white;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 8px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const VisualizationContainer = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 15px;
  padding: 30px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  min-width: 600px;
  min-height: 500px;
  position: relative;
`;

const DataStructureTitle = styled.h2`
  color: white;
  text-align: center;
  margin-bottom: 20px;
  font-size: 1.8rem;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
`;

const StackContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  margin-bottom: 30px;
`;

const QueueContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 5px;
  justify-content: center;
  flex-wrap: wrap;
`;

const Element = styled.div`
  width: 60px;
  height: 60px;
  background: ${props => {
    if (props.$isHighlighted) return '#ffd43b';
    if (props.$isNew) return '#51cf66';
    if (props.$isRemoved) return '#ff6b6b';
    return '#4ecdc4';
  }};
  border: 3px solid white;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 1.2rem;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  
  &:hover {
    transform: scale(1.1);
  }
`;

const Arrow = styled.div`
  color: white;
  font-size: 1.5rem;
  font-weight: bold;
  margin: 0 10px;
`;

const InfoPanel = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 15px;
  margin-top: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const InfoText = styled.p`
  color: white;
  margin: 5px 0;
  font-size: 0.9rem;
`;

const StatsContainer = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 15px;
`;

const StatBox = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 10px;
  text-align: center;
  flex: 1;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const StatValue = styled.div`
  color: #4ecdc4;
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  color: white;
  font-size: 0.8rem;
  opacity: 0.8;
`;

const OperationLog = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 15px;
  margin-top: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  max-height: 150px;
  overflow-y: auto;
`;

const LogTitle = styled.h3`
  color: white;
  margin-bottom: 10px;
  font-size: 1rem;
`;

const LogEntry = styled.div`
  color: white;
  font-size: 0.8rem;
  margin: 3px 0;
  padding: 5px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 5px;
`;

function StackQueuePage() {
  // Stack state
  const [stack, setStack] = useState([]);
  const [stackInput, setStackInput] = useState('');
  
  // Queue state
  const [queue, setQueue] = useState([]);
  const [queueInput, setQueueInput] = useState('');
  
  // Animation state
  const [highlightedElement, setHighlightedElement] = useState(null);
  const [newElement, setNewElement] = useState(null);
  const [removedElement, setRemovedElement] = useState(null);
  const [operationLog, setOperationLog] = useState([]);
  
  // Animation delay
  const [animationSpeed, setAnimationSpeed] = useState(1000);
  
  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
  
  const addToLog = (message) => {
    const timestamp = new Date().toLocaleTimeString();
    setOperationLog(prev => [...prev.slice(-9), `${timestamp}: ${message}`]);
  };
  
  // Stack operations
  const pushToStack = async () => {
    if (!stackInput.trim()) return;
    
    const value = stackInput.trim();
    setStackInput('');
    
    // Highlight new element
    setNewElement(value);
    addToLog(`Pushed "${value}" to stack`);
    
    await delay(animationSpeed);
    
    setStack(prev => [...prev, value]);
    setNewElement(null);
  };
  
  const popFromStack = async () => {
    if (stack.length === 0) {
      addToLog('Stack is empty - cannot pop');
      return;
    }
    
    const value = stack[stack.length - 1];
    
    // Highlight element being removed
    setRemovedElement(value);
    addToLog(`Popped "${value}" from stack`);
    
    await delay(animationSpeed);
    
    setStack(prev => prev.slice(0, -1));
    setRemovedElement(null);
  };
  
  const peekStack = () => {
    if (stack.length === 0) {
      addToLog('Stack is empty - nothing to peek');
      return;
    }
    
    const value = stack[stack.length - 1];
    setHighlightedElement(value);
    addToLog(`Peeked at "${value}" (top of stack)`);
    
    setTimeout(() => setHighlightedElement(null), 2000);
  };
  
  const clearStack = () => {
    if (stack.length === 0) {
      addToLog('Stack is already empty');
      return;
    }
    
    setStack([]);
    addToLog('Stack cleared');
  };
  
  // Queue operations
  const enqueue = async () => {
    if (!queueInput.trim()) return;
    
    const value = queueInput.trim();
    setQueueInput('');
    
    // Highlight new element
    setNewElement(value);
    addToLog(`Enqueued "${value}" to queue`);
    
    await delay(animationSpeed);
    
    setQueue(prev => [...prev, value]);
    setNewElement(null);
  };
  
  const dequeue = async () => {
    if (queue.length === 0) {
      addToLog('Queue is empty - cannot dequeue');
      return;
    }
    
    const value = queue[0];
    
    // Highlight element being removed
    setRemovedElement(value);
    addToLog(`Dequeued "${value}" from queue`);
    
    await delay(animationSpeed);
    
    setQueue(prev => prev.slice(1));
    setRemovedElement(null);
  };
  
  const peekQueue = () => {
    if (queue.length === 0) {
      addToLog('Queue is empty - nothing to peek');
      return;
    }
    
    const value = queue[0];
    setHighlightedElement(value);
    addToLog(`Peeked at "${value}" (front of queue)`);
    
    setTimeout(() => setHighlightedElement(null), 2000);
  };
  
  const clearQueue = () => {
    if (queue.length === 0) {
      addToLog('Queue is already empty');
      return;
    }
    
    setQueue([]);
    addToLog('Queue cleared');
  };
  
  // Generate random elements
  const generateRandomStack = () => {
    const randomElements = Array.from({ length: Math.floor(Math.random() * 8) + 3 }, () => 
      Math.floor(Math.random() * 100).toString()
    );
    setStack(randomElements);
    addToLog(`Generated random stack with ${randomElements.length} elements`);
  };
  
  const generateRandomQueue = () => {
    const randomElements = Array.from({ length: Math.floor(Math.random() * 8) + 3 }, () => 
      Math.floor(Math.random() * 100).toString()
    );
    setQueue(randomElements);
    addToLog(`Generated random queue with ${randomElements.length} elements`);
  };
  
  return (
    <PageContainer>
      <LeftPanel>
        <Title>Stack & Queue</Title>
        
        <Controls>
          {/* Stack Controls */}
          <ControlGroup>
            <Label>Stack Operations</Label>
            <Input
              type="text"
              placeholder="Enter value to push"
              value={stackInput}
              onChange={(e) => setStackInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && pushToStack()}
            />
            <Button onClick={pushToStack}>Push to Stack</Button>
            <Button onClick={popFromStack} disabled={stack.length === 0}>Pop from Stack</Button>
            <Button onClick={peekStack} disabled={stack.length === 0}>Peek Stack</Button>
            <Button onClick={clearStack} disabled={stack.length === 0} $variant="danger">Clear Stack</Button>
            <Button onClick={generateRandomStack}>Generate Random Stack</Button>
          </ControlGroup>
          
          {/* Queue Controls */}
          <ControlGroup>
            <Label>Queue Operations</Label>
            <Input
              type="text"
              placeholder="Enter value to enqueue"
              value={queueInput}
              onChange={(e) => setQueueInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && enqueue()}
            />
            <Button onClick={enqueue}>Enqueue</Button>
            <Button onClick={dequeue} disabled={queue.length === 0}>Dequeue</Button>
            <Button onClick={peekQueue} disabled={queue.length === 0}>Peek Queue</Button>
            <Button onClick={clearQueue} disabled={queue.length === 0} $variant="danger">Clear Queue</Button>
            <Button onClick={generateRandomQueue}>Generate Random Queue</Button>
          </ControlGroup>
          
          {/* Animation Speed Control */}
          <ControlGroup>
            <Label>Animation Speed: {animationSpeed}ms</Label>
            <Input
              type="range"
              min="500"
              max="3000"
              value={animationSpeed}
              onChange={(e) => setAnimationSpeed(parseInt(e.target.value))}
            />
          </ControlGroup>
          
          {/* Stats */}
          <StatsContainer>
            <StatBox>
              <StatValue>{stack.length}</StatValue>
              <StatLabel>Stack Size</StatLabel>
            </StatBox>
            <StatBox>
              <StatValue>{queue.length}</StatValue>
              <StatLabel>Queue Size</StatLabel>
            </StatBox>
          </StatsContainer>
          
          {/* Operation Log */}
          <OperationLog>
            <LogTitle>Operation Log</LogTitle>
            {operationLog.map((entry, index) => (
              <LogEntry key={index}>{entry}</LogEntry>
            ))}
          </OperationLog>
        </Controls>
      </LeftPanel>
      
      <RightPanel>
        <VisualizationContainer>
          {/* Stack Visualization */}
          <DataStructureTitle>Stack (LIFO - Last In, First Out)</DataStructureTitle>
          <StackContainer>
            {stack.length === 0 ? (
              <InfoText style={{ color: 'rgba(255, 255, 255, 0.7)', fontStyle: 'italic' }}>
                Stack is empty
              </InfoText>
            ) : (
              stack.map((element, index) => (
                <Element
                  key={`${element}-${index}`}
                  $isHighlighted={highlightedElement === element}
                  $isNew={newElement === element}
                  $isRemoved={removedElement === element}
                >
                  {element}
                </Element>
              ))
            )}
          </StackContainer>
          
          {/* Queue Visualization */}
          <DataStructureTitle>Queue (FIFO - First In, First Out)</DataStructureTitle>
          <QueueContainer>
            {queue.length === 0 ? (
              <InfoText style={{ color: 'rgba(255, 255, 255, 0.7)', fontStyle: 'italic' }}>
                Queue is empty
              </InfoText>
            ) : (
              <>
                <Arrow>→</Arrow>
                {queue.map((element, index) => (
                  <Element
                    key={`${element}-${index}`}
                    $isHighlighted={highlightedElement === element}
                    $isNew={newElement === element}
                    $isRemoved={removedElement === element}
                  >
                    {element}
                  </Element>
                ))}
                <Arrow>→</Arrow>
              </>
            )}
          </QueueContainer>
          
          {/* Info Panel */}
          <InfoPanel>
            <InfoText><strong>Stack:</strong> Elements are added and removed from the top (LIFO principle)</InfoText>
            <InfoText><strong>Queue:</strong> Elements are added at the back and removed from the front (FIFO principle)</InfoText>
            <InfoText><strong>Operations:</strong> Use the controls on the left to interact with both data structures</InfoText>
          </InfoPanel>
        </VisualizationContainer>
      </RightPanel>
    </PageContainer>
  );
}

export default StackQueuePage;
